import Button from '@cobliteam/colib-ui/Button';
import Icon from '@cobliteam/colib-ui/Icon';
import { Location } from '@cobliteam/common-types/src/location';
import type { Preferences } from '@cobliteam/common-types/src/preferences';
import { DatadogTrack } from '@cobliteam/datadog-rum';
import { useLatestNews } from '@cobliteam/hooks/src/useEntity/usePrismic';
import { filterNewsByFeatureFlags } from '@cobliteam/hooks/src/useEntity/usePrismic/newsFeatureFlags';
import useFeatureFlags from '@cobliteam/hooks/src/useFeatureFlags';
import useLocalStorage from '@cobliteam/hooks/src/useLocalStorage';
import { useLanguage, useTranslation } from '@cobliteam/i18n';
import TooltipOnIntent from '@cobliteam/ui-business/components/TooltipOnIntent/TooltipOnIntent';
import { lazy, Suspense, useRef, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useHistory } from 'react-router-dom';
import { getAnimatedIcon } from '../../AppSidebar/components/animatedIcons';

const NewsFeedPanel = lazy(() => import('./NewsFeedPanel'));
const NEWS_ITEMS_LIMIT = 10;
// Busca mais do que exibe porque o filtro de feature flag roda depois da página do Prismic.
const NEWS_FETCH_LIMIT = 30;

const getNewsTimestamp = (date: string | undefined) => {
  if (!date) return 0;
  const timestamp = new Date(date).getTime();
  return Number.isNaN(timestamp) ? 0 : timestamp;
};

const NewsFeedFallback = () => {
  const { t } = useTranslation('common');
  return (
    <Button variant='secondary' size='icon-sm' aria-label={t('news_feed.open')}>
      <Icon size='sm' name='x' />
    </Button>
  );
};

const NewsFeedContent = () => {
  const history = useHistory();
  const { t } = useTranslation('common');
  const { currentLanguage } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const [hasOpened, setHasOpened] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [isHovered, setIsHovered] = useState(false);
  const { data: news = [], isLoading, error } = useLatestNews({ limit: NEWS_FETCH_LIMIT });
  const featureFlags = useFeatureFlags();
  const [preferences, setPreferences] = useLocalStorage<Preferences>('preferences');

  const recentNews = filterNewsByFeatureFlags(news, featureFlags)
    .sort((left, right) => getNewsTimestamp(right.last_publication_date) - getNewsTimestamp(left.last_publication_date))
    .slice(0, NEWS_ITEMS_LIMIT);
  const latestNewsTimestamp = getNewsTimestamp(recentNews[0]?.last_publication_date);
  const storedLastOpenedTimestamp = preferences?.lastNewsFeedOpenedDate ? Number(preferences.lastNewsFeedOpenedDate) : 0;
  const lastOpenedTimestamp = Number.isFinite(storedLastOpenedTimestamp) ? storedLastOpenedTimestamp : 0;
  const [highlightAfterTimestamp, setHighlightAfterTimestamp] = useState(lastOpenedTimestamp);
  const hasUnreadNews = latestNewsTimestamp > lastOpenedTimestamp;

  const handleOpenChange = (nextOpen: boolean) => {
    setIsOpen(nextOpen);
    if (nextOpen) {
      setHasOpened(true);
      setHighlightAfterTimestamp(lastOpenedTimestamp);
      setPreferences({ ...preferences, lastNewsFeedOpenedDate: latestNewsTimestamp || Date.now() });
    }
  };

  const handleSeeAll = () => {
    handleOpenChange(false);
    history.push(Location.NEWS);
  };

  return (
    <>
      <TooltipOnIntent
        disabled={isOpen}
        render={
          <div className='relative mt-0.5' onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)}>
            <Button
              variant={isOpen || !hasUnreadNews ? 'secondary' : 'ghost'}
              size='icon-sm'
              ref={triggerRef}
              data-testid='app-header-newslist-button'
              aria-label={t('news_feed.open')}
              aria-expanded={isOpen}
              onClick={() => handleOpenChange(true)}
            >
              {getAnimatedIcon({ icon: 'gift', isHovered })}
            </Button>
            {hasUnreadNews ? (
              <span className='absolute -top-0.5 -right-0.5 flex size-2' aria-hidden='true'>
                <span className='absolute inline-flex h-full w-full animate-ping rounded-full bg-primary opacity-75 motion-reduce:animate-none' />
                <span className='relative inline-flex size-2 rounded-full bg-primary' />
              </span>
            ) : null}
          </div>
        }
        contentProps={{ side: 'right' }}
      >
        {t('news_feed.title')}
      </TooltipOnIntent>

      {hasOpened ? (
        <Suspense fallback={null}>
          <NewsFeedPanel
            open={isOpen}
            onOpenChange={handleOpenChange}
            onSeeAll={handleSeeAll}
            recentNews={recentNews}
            isLoading={isLoading}
            error={!!error}
            highlightAfterTimestamp={highlightAfterTimestamp}
            locale={currentLanguage}
            triggerRef={triggerRef}
          />
        </Suspense>
      ) : null}
    </>
  );
};

const NewsFeed = () => {
  return (
    <ErrorBoundary
      fallback={<NewsFeedFallback />}
      onError={(error, errorInfo) => {
        DatadogTrack.error(error, {
          component: 'NewsFeed',
          section: 'AppHeader',
          errorInfo,
        });
      }}
    >
      <NewsFeedContent />
    </ErrorBoundary>
  );
};

export default NewsFeed;
